/**
 * TeamLeader Workspace — Mentoring Students Page
 *
 * Mentoring-first roster across every group the leader leads.
 *
 * The page is a LIST by default rather than a card grid. Six summary tiles plus a filter row used
 * to push the roster ~430px down the page and only six students fitted on a screen; the counts now
 * ride in one 68px signal band (where each count is still the filter it describes), and the roster
 * itself is a dense row list that fits roughly twice as many students per screen. The card grid is
 * still one click away behind the list/cards toggle — it reads better when a mentor is browsing
 * rather than scanning.
 */
import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState, useMemo, type ComponentType, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { fetchMyGroups, type TeamLeaderGroup } from "@/lib/api/teamleader";
import {
  fetchStudentGrid,
  fetchStudentStatusCounts,
  type StudentFilterQueryParams,
  type StudentGridRow,
} from "@/lib/api/students";
import { PaginationControls } from "@/components/PaginationControls";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  GraduationCap,
  Search,
  Clock,
  MessageCircle,
  ChevronRight,
  BookOpen,
  Users,
  AlertTriangle,
  X,
  ArrowUpDown,
  ArrowRight,
  LayoutGrid,
  List,
  NotebookPen,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/team-leader/students")({
  component: MentoringStudentsPage,
});

/** `InTraining` → `In Training`. The grid returns raw enum names; the roster shouldn't show them. */
function humanizeStatus(status: string | null | undefined): string {
  if (!status) return "Active";
  return status.replace(/([a-z0-9])([A-Z])/g, "$1 $2").trim();
}

/**
 * How long ago the student was last seen — "just now" / "3h ago" / "yesterday", or a date for
 * anything over a month. A bare `toLocaleDateString()` gave "Active 7/25/2026", which reads as a
 * future-looking date rather than recency.
 *
 * Returns null when there is no timestamp at all; the caller states that case through the presence
 * label ("Never signed in") rather than dressing it up as an elapsed time.
 */
function formatSince(lastSeenAt: string | null | undefined): string | null {
  if (!lastSeenAt) return null;
  const then = new Date(lastSeenAt).getTime();
  if (Number.isNaN(then)) return null;

  const minutes = Math.floor((Date.now() - then) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return new Date(lastSeenAt).toLocaleDateString();
}

// ── Filters ──────────────────────────────────────────────────────────────────
//
// Every option maps to a facet `StudentFilterQuery` actually supports server-side, so a filter can
// never disagree with the roster it produces. The old "At Risk Only" option sent
// `isBlacklisted: true` alone while the cards flagged risk as `isBlacklisted || isNotResponding`
// — a not-responding student was flagged on their own card yet hidden by the filter meant to find
// them, and the dashboard's "need attention" count (which sums both) led here and disagreed. That
// OR is now a real server facet (`needsAttention`), with each flag still available on its own.
const STATUS_FILTERS = {
  all: { label: "All students", params: {} },
  "needs-attention": { label: "Needs attention", params: { needsAttention: true } },
  blacklisted: { label: "Blacklisted", params: { isBlacklisted: true } },
  "not-responding": { label: "Not responding", params: { isNotResponding: true } },
  // The three presence states are separate options, not one "activity" multi-select: the server
  // ORs within the `activity` facet, so a single value per option is what makes each roster exact.
  online: { label: "Online now", params: { activity: ["Online"] } },
  offline: { label: "Offline", params: { activity: ["Offline"] } },
  "never-signed-in": { label: "Never signed in", params: { activity: ["NeverActive"] } },
  "in-training": { label: "In training", params: { trainingStatus: ["InTraining"] } },
  passed: { label: "Passed", params: { trainingStatus: ["Passed"] } },
  failed: { label: "Failed", params: { trainingStatus: ["Failed"] } },
} satisfies Record<string, { label: string; params: StudentFilterQueryParams }>;

type StatusFilterKey = keyof typeof STATUS_FILTERS;

/**
 * `?filter=` values that predate the options above. The TeamLeader dashboard's stat cards link
 * here with these, and they may also be bookmarked — silently falling back to "all" would make a
 * drill-down look like it did nothing.
 */
const LEGACY_FILTER_ALIASES: Record<string, StatusFilterKey> = {
  "at-risk": "needs-attention",
  "active-today": "online",
};

/**
 * Sort options are limited to the four the grid can actually order by (`name`, `lastseen`,
 * `trainingstatus`, and its `createdAt` default). Progress is deliberately absent: it is computed
 * per row after paging, so a "sort by progress" option would only reorder the current page.
 */
const SORT_OPTIONS = {
  newest: { label: "Recently added", params: { sortBy: "createdAt", sortDir: "desc" } },
  "name-asc": { label: "Name (A–Z)", params: { sortBy: "name", sortDir: "asc" } },
  "name-desc": { label: "Name (Z–A)", params: { sortBy: "name", sortDir: "desc" } },
  "last-active": { label: "Last active", params: { sortBy: "lastseen", sortDir: "desc" } },
  status: { label: "Training status", params: { sortBy: "trainingstatus", sortDir: "asc" } },
} satisfies Record<string, { label: string; params: StudentFilterQueryParams }>;

type SortKey = keyof typeof SORT_OPTIONS;

// ── Presence ─────────────────────────────────────────────────────────────────
//
// `StudentGridRow.activity` is the server's `ActivityState`, derived from LastSeenAt against a
// 5-minute window: Online (seen inside it), Offline (seen before it), NeverActive (no LastSeenAt at
// all — the account exists but has never been signed into). All three are distinct facts a mentor
// acts on differently, so all three get a name and a colour here; the card previously rendered a
// dot for Online only and left the other two to be inferred from the last-seen sentence.
const PRESENCE = {
  Online: {
    label: "Online",
    dot: "bg-success",
    text: "text-success",
    hint: "Seen in the last 5 minutes",
  },
  Offline: {
    label: "Offline",
    dot: "bg-muted-foreground/50",
    text: "text-muted-foreground",
    hint: "Has signed in before, but not in the last 5 minutes",
  },
  NeverActive: {
    label: "Never signed in",
    dot: "border border-warning bg-transparent",
    text: "text-warning",
    hint: "This account has never been signed into",
  },
} as const;

type PresenceKey = keyof typeof PRESENCE;

/** Unknown/absent `activity` values fall back to Offline rather than inventing a presence. */
function presenceOf(activity: string | null | undefined) {
  return PRESENCE[(activity as PresenceKey) in PRESENCE ? (activity as PresenceKey) : "Offline"];
}

/** Colour band for a progress bar. Deliberately coarse — it's a glanceable signal, not a grade. */
function progressTone(pct: number): string {
  if (pct >= 75) return "[&>div]:bg-success";
  if (pct >= 40) return "[&>div]:bg-primary";
  if (pct > 0) return "[&>div]:bg-warning";
  return "[&>div]:bg-muted-foreground/40";
}

/** `progressPercent` is a 0–100 double by contract, but a stale cached row can carry anything. */
function safePercent(value: unknown): number {
  const raw = Number(value);
  return Number.isFinite(raw) ? Math.round(Math.min(100, Math.max(0, raw))) : 0;
}

/**
 * The one-line summary of a student's exam standing, shared by the row and the card.
 *
 * `examPassed` is the authoritative result; `examScore` can exist without it (a scored attempt
 * that has not been marked pass/fail), and neither existing means untouched rather than zero.
 */
function examSummary(student: StudentGridRow): { label: string; sub?: string; tone: string } {
  if (student.examPassed != null) {
    return {
      label: student.examScore != null ? `${Math.round(student.examScore)}%` : "—",
      sub: student.examPassed ? "Passed" : "Failed",
      tone: student.examPassed ? "bg-success" : "bg-destructive",
    };
  }
  if (student.examScore != null) {
    return { label: `${Math.round(student.examScore)}%`, tone: "bg-warning" };
  }
  return { label: "Not attempted", tone: "bg-muted-foreground/40" };
}

/**
 * The chip beside a student's name.
 *
 * Risk outranks everything. Otherwise the training status only earns a chip when it is NOT the
 * unremarkable "In Training" default — a roster where every row carries the same grey badge spends
 * horizontal space to say nothing, while Passed / Failed / Withdrawn are exactly what a mentor
 * scans for.
 */
function StatusChip({ student }: { student: StudentGridRow }) {
  if (student.isBlacklisted) {
    return (
      <Badge variant="destructive" className="shrink-0 px-1.5 text-[10px] leading-4">
        Blacklisted
      </Badge>
    );
  }
  if (student.isNotResponding) {
    return (
      <Badge className="shrink-0 border-transparent bg-warning-muted px-1.5 text-[10px] leading-4 text-warning hover:bg-warning-muted">
        Not responding
      </Badge>
    );
  }
  const status = student.trainingStatus;
  if (!status || status === "InTraining") return null;
  return (
    <Badge variant="secondary" className="shrink-0 px-1.5 text-[10px] leading-4">
      {humanizeStatus(status)}
    </Badge>
  );
}

/**
 * One count in the signal band. Doubles as a filter shortcut: the counts a team leader cares about
 * are also the views they want to open, so the number and the way in are the same control rather
 * than a read-only stat plus a hunt through a dropdown.
 */
function SignalChip({
  dot,
  label,
  value,
  hint,
  isLoading,
  isActive,
  onClick,
}: {
  /** Utility classes for the state dot, so presence colour is stated once in `PRESENCE`. */
  dot: string;
  label: string;
  /** `undefined` renders an em dash — a failed counts fetch must not read as a real zero. */
  value: number | undefined;
  hint?: string;
  isLoading: boolean;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      title={hint}
      className={[
        "flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-left transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        isActive ? "bg-primary/10 ring-1 ring-primary/30" : "hover:bg-muted/60",
      ].join(" ")}
    >
      <span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} aria-hidden="true" />
      {isLoading ? (
        <Skeleton className="h-4 w-6" />
      ) : (
        <span className="text-sm font-bold tabular-nums">{value ?? "—"}</span>
      )}
      <span className="whitespace-nowrap text-xs text-muted-foreground">{label}</span>
    </button>
  );
}

/** Avatar plus the presence dot, at the two sizes the roster uses. */
function StudentAvatar({
  student,
  presence,
  className,
}: {
  student: StudentGridRow;
  presence: (typeof PRESENCE)[PresenceKey];
  className?: string;
}) {
  return (
    <div className="relative shrink-0">
      <Avatar className={className ?? "h-9 w-9"}>
        {student.avatarUrl && <AvatarImage src={student.avatarUrl} alt="" />}
        <AvatarFallback
          className="text-xs font-bold text-white"
          style={{ background: "var(--gradient-brand)" }}
        >
          {(student.fullName || student.email || "?").charAt(0).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      {/* A dot for every state, not just Online. A dot that only ever appears for online students
          makes "offline" and "never signed in" look identical — both were just an absent dot. */}
      <span
        className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-card ${presence.dot}`}
        title={`${presence.label} — ${presence.hint}`}
      />
      <span className="sr-only">{presence.label}</span>
    </div>
  );
}

/** Column widths for the list view. Stated once so the header row can never drift from the rows. */
const ROW_GRID =
  "md:grid-cols-[minmax(0,2.4fr)_minmax(0,1.5fr)_minmax(0,1.7fr)_minmax(0,1.1fr)_minmax(0,1fr)_auto]";

/**
 * One roster row.
 *
 * The whole row opens the profile via a "stretched link": a real <Link> on the name whose ::after
 * covers the row. The trailing controls sit above that overlay on `relative z-10`, so they stay
 * separately clickable — and because the link is a real href, middle-click and open-in-new-tab
 * work, which a `role="button"` div could never offer.
 */
function StudentRow({
  student,
  groupMap,
  onChat,
}: {
  student: StudentGridRow;
  groupMap: Map<string, TeamLeaderGroup>;
  onChat: () => void;
}) {
  const groups = student.groups ?? [];
  const primaryGroup = groups[0];
  const groupName = primaryGroup?.groupName || "Unassigned";
  const groupInfo = primaryGroup ? groupMap.get(primaryGroup.groupId) : null;
  const groupTracks = groupInfo?.tracks?.map((t) => t.name) ?? [];
  const displayName = student.fullName || student.email.split("@")[0];
  const progressPct = safePercent(student.progressPercent);
  const presence = presenceOf(student.activity);
  const since = formatSince(student.lastSeenAt);
  const exam = examSummary(student);

  return (
    <div
      className={`group relative grid grid-cols-1 items-start gap-3 border-b p-3 transition-colors last:border-b-0 hover:bg-muted/40 focus-within:bg-muted/40 md:min-h-14 md:items-center md:gap-4 md:px-4 md:py-2 ${ROW_GRID}`}
    >
      {/* Student */}
      <div className="flex min-w-0 items-center gap-3">
        <StudentAvatar student={student} presence={presence} />
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-1.5">
            <Link
              to="/team-leader/students/$studentId"
              params={{ studentId: student.id }}
              className="truncate text-sm font-semibold outline-none transition-colors after:absolute after:inset-0 after:content-[''] hover:text-primary focus-visible:text-primary"
            >
              {displayName}
            </Link>
            <StatusChip student={student} />
          </div>
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-xs text-muted-foreground">{student.email}</span>
            {student.studentCode && (
              <span className="shrink-0 font-mono text-[10px] text-muted-foreground/70">
                {student.studentCode}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Group & track. A student can belong to several groups; showing only the first read as if
          that were the whole story, so the extras are counted explicitly. */}
      <div className="min-w-0 pl-12 md:pl-0">
        <p
          className="truncate text-[13px] font-medium"
          title={groups.map((g) => g.groupName).join(", ")}
        >
          {groupName}
          {groups.length > 1 && (
            <span className="ml-1 font-normal text-muted-foreground">+{groups.length - 1}</span>
          )}
        </p>
        <p className="truncate text-[11px] text-muted-foreground" title={groupTracks.join(", ")}>
          {groupTracks.length > 0 ? (
            <>
              {groupTracks[0]}
              {groupTracks.length > 1 && <span className="ml-1">+{groupTracks.length - 1}</span>}
            </>
          ) : (
            "No track"
          )}
        </p>
      </div>

      {/* Module progress */}
      <div className="flex min-w-0 items-center gap-2.5 pl-12 md:pl-0">
        <Progress
          value={progressPct}
          className={`h-1.5 flex-1 bg-muted ${progressTone(progressPct)}`}
          aria-label={`${progressPct}% of modules complete`}
        />
        <span className="w-8 shrink-0 text-right text-xs font-bold tabular-nums">
          {progressPct}%
        </span>
        <span className="w-10 shrink-0 text-[11px] tabular-nums text-muted-foreground/70">
          {student.completedModules ?? 0}/{student.totalModules ?? 0}
        </span>
      </div>

      {/* Exam */}
      <div className="flex min-w-0 items-center gap-2 pl-12 md:pl-0">
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${exam.tone}`} aria-hidden="true" />
        <span className="truncate text-xs">
          <span className={exam.sub ? "font-bold" : "text-muted-foreground"}>{exam.label}</span>
          {exam.sub && <span className="ml-1 text-muted-foreground">{exam.sub}</span>}
        </span>
      </div>

      {/* Presence, stated in words with the elapsed time as the secondary detail. "Active 3h ago"
          alone never said whether the system considers them online or offline, and an account that
          has never been signed into read the same as a stale one. */}
      <div
        className="min-w-0 pl-12 md:pl-0"
        title={
          student.lastSeenAt
            ? `${presence.hint}. Last seen ${new Date(student.lastSeenAt).toLocaleString()}`
            : presence.hint
        }
      >
        <p className={`truncate text-xs font-semibold ${presence.text}`}>{presence.label}</p>
        <p className="truncate text-[11px] text-muted-foreground/70">{since ?? "no sign-in yet"}</p>
      </div>

      {/* Actions sit above the stretched link's overlay. */}
      <div className="relative z-10 flex items-center justify-end gap-0.5 pl-12 md:pl-0">
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 text-muted-foreground hover:text-foreground"
          onClick={onChat}
          title="Send a direct message"
        >
          <MessageCircle className="h-4 w-4" />
          <span className="sr-only">Message {displayName}</span>
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 text-muted-foreground hover:text-foreground"
          asChild
          title="Log a call, message or meeting"
        >
          <Link
            to="/team-leader/students/$studentId"
            params={{ studentId: student.id }}
            search={{ tab: "crm" }}
          >
            <NotebookPen className="h-4 w-4" />
            <span className="sr-only">Log contact for {displayName}</span>
          </Link>
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 text-muted-foreground hover:text-foreground"
          asChild
          title="Open profile"
        >
          <Link to="/team-leader/students/$studentId" params={{ studentId: student.id }}>
            <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            <span className="sr-only">Open {displayName}'s profile</span>
          </Link>
        </Button>
      </div>
    </div>
  );
}

/** The list's column header. Hidden below `md`, where the rows stack instead of aligning. */
function RosterHeaderRow() {
  const cell = "text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70";
  return (
    <div
      className={`hidden border-b bg-muted/30 px-4 py-2 md:grid md:items-center md:gap-4 ${ROW_GRID}`}
    >
      <span className={cell}>Student</span>
      <span className={cell}>Group &amp; track</span>
      <span className={cell}>Module progress</span>
      <span className={cell}>Exam</span>
      <span className={cell}>Presence</span>
      <span className="sr-only">Actions</span>
    </div>
  );
}

/**
 * One student card — the browsing view behind the list/cards toggle.
 *
 * Same stretched-link construction as the row: a real <Link> on the name whose ::after covers the
 * card, with the footer controls lifted above it.
 */
function StudentCard({
  student,
  groupMap,
  onChat,
}: {
  student: StudentGridRow;
  groupMap: Map<string, TeamLeaderGroup>;
  onChat: () => void;
}) {
  const isAtRisk = student.isBlacklisted || student.isNotResponding;
  const groups = student.groups ?? [];
  const primaryGroup = groups[0];
  const groupName = primaryGroup?.groupName || "Unassigned";
  const groupInfo = primaryGroup ? groupMap.get(primaryGroup.groupId) : null;
  const groupTracks = groupInfo?.tracks?.map((t) => t.name) ?? [];
  const displayName = student.fullName || student.email.split("@")[0];
  const progressPct = safePercent(student.progressPercent);
  const presence = presenceOf(student.activity);
  const since = formatSince(student.lastSeenAt);
  const hasExamResult = student.examPassed != null || student.examScore != null;

  return (
    <Card
      className={[
        "group relative flex flex-col overflow-hidden border-l-4 transition-all duration-200",
        "hover:border-primary/40 hover:shadow-lg focus-within:ring-2 focus-within:ring-ring",
        isAtRisk
          ? "border-l-destructive"
          : progressPct >= 75
            ? "border-l-success"
            : "border-l-transparent",
      ].join(" ")}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <StudentAvatar student={student} presence={presence} className="h-11 w-11" />
            <div className="min-w-0">
              <Link
                to="/team-leader/students/$studentId"
                params={{ studentId: student.id }}
                className="block truncate text-base font-bold outline-none transition-colors after:absolute after:inset-0 after:content-[''] hover:text-primary focus-visible:text-primary"
              >
                {displayName}
              </Link>
              <p className="truncate text-xs text-muted-foreground">{student.email}</p>
              {student.studentCode && (
                <p className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground/70">
                  {student.studentCode}
                </p>
              )}
            </div>
          </div>

          <Badge
            variant={isAtRisk ? "destructive" : "secondary"}
            className="shrink-0 text-[10px]"
            title={
              student.isBlacklisted && student.isNotResponding
                ? "Blacklisted and not responding"
                : undefined
            }
          >
            {isAtRisk
              ? student.isBlacklisted
                ? "Blacklisted"
                : "Not Responding"
              : humanizeStatus(student.trainingStatus)}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col gap-4">
        <div className="space-y-1.5 rounded-lg bg-muted/50 p-3 text-xs">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <Users className="h-3.5 w-3.5" /> Group
            </span>
            <span
              className="truncate font-semibold text-foreground"
              title={groups.map((g) => g.groupName).join(", ")}
            >
              {groupName}
              {groups.length > 1 && (
                <span className="ml-1 font-normal text-muted-foreground">+{groups.length - 1}</span>
              )}
            </span>
          </div>
          {groupTracks.length > 0 && (
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <BookOpen className="h-3.5 w-3.5" /> Track
              </span>
              <span
                className="max-w-[160px] truncate font-medium text-foreground"
                title={groupTracks.join(", ")}
              >
                {groupTracks[0]}
                {groupTracks.length > 1 && (
                  <span className="ml-1 font-normal text-muted-foreground">
                    +{groupTracks.length - 1}
                  </span>
                )}
              </span>
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-semibold text-foreground tabular-nums">
              {progressPct}%
              <span className="ml-1 font-normal text-muted-foreground">
                ({student.completedModules ?? 0}/{student.totalModules ?? 0} modules)
              </span>
            </span>
          </div>
          <Progress value={progressPct} className={`h-1.5 bg-muted ${progressTone(progressPct)}`} />
        </div>

        {hasExamResult && (
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="text-muted-foreground">Exams</span>
            <span className="flex items-center gap-2">
              {student.examScore != null && (
                <span className="font-semibold tabular-nums">{Math.round(student.examScore)}%</span>
              )}
              {student.examPassed != null && (
                <Badge
                  variant={student.examPassed ? "default" : "destructive"}
                  className="text-[10px]"
                >
                  {student.examPassed ? "Passed" : "Failed"}
                </Badge>
              )}
            </span>
          </div>
        )}

        <div className="relative z-10 mt-auto flex items-center justify-between gap-2 border-t pt-3 text-xs">
          <span
            className="flex min-w-0 items-center gap-1.5"
            title={
              student.lastSeenAt
                ? `${presence.hint}. Last seen ${new Date(student.lastSeenAt).toLocaleString()}`
                : presence.hint
            }
          >
            <span className={`h-2 w-2 shrink-0 rounded-full ${presence.dot}`} aria-hidden="true" />
            <span className={`font-medium ${presence.text}`}>{presence.label}</span>
            {since && (
              <span className="flex min-w-0 items-center gap-1 text-muted-foreground">
                <Clock className="h-3 w-3 shrink-0" />
                <span className="truncate">{since}</span>
              </span>
            )}
          </span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={onChat}>
              <MessageCircle className="h-3.5 w-3.5" />
              Chat
            </Button>
            <Button size="sm" variant="ghost" className="h-7 gap-0.5 px-2 text-xs" asChild>
              <Link to="/team-leader/students/$studentId" params={{ studentId: student.id }}>
                Profile
                <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Skeletons that mirror whichever view is about to render, so the page doesn't jump on load. */
function RosterSkeleton({ view }: { view: RosterView }) {
  if (view === "cards") {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} className="overflow-hidden">
            <CardContent className="space-y-4 p-6">
              <div className="flex items-center gap-3">
                <Skeleton className="h-11 w-11 rounded-full" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
              </div>
              <Skeleton className="h-16 w-full rounded-lg" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }
  return (
    <Card className="overflow-hidden">
      <RosterHeaderRow />
      {Array.from({ length: 10 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 border-b p-3 last:border-b-0 md:px-4">
          <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-40" />
            <Skeleton className="h-3 w-56" />
          </div>
          <Skeleton className="hidden h-2 w-32 md:block" />
          <Skeleton className="hidden h-3 w-20 md:block" />
        </div>
      ))}
    </Card>
  );
}

type RosterView = "list" | "cards";

function ViewToggle({ view, onChange }: { view: RosterView; onChange: (v: RosterView) => void }) {
  const option = (
    value: RosterView,
    label: string,
    Icon: ComponentType<{ className?: string }>,
  ) => (
    <button
      type="button"
      onClick={() => onChange(value)}
      aria-pressed={view === value}
      title={label}
      className={[
        "flex h-7 w-8 items-center justify-center rounded-[6px] transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        view === value
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:text-foreground",
      ].join(" ")}
    >
      <Icon className="h-4 w-4" />
      <span className="sr-only">{label}</span>
    </button>
  );
  return (
    <div className="flex shrink-0 items-center gap-0.5 rounded-md border bg-background p-0.5 shadow-sm">
      {option("list", "List view", List)}
      {option("cards", "Card view", LayoutGrid)}
    </div>
  );
}

function MentoringStudentsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const searchParams = (useSearch({ strict: false }) ?? {}) as { filter?: string };
  const rawFilter = searchParams.filter;
  const initialFilter: StatusFilterKey =
    (rawFilter ? LEGACY_FILTER_ALIASES[rawFilter] : undefined) ??
    (rawFilter && rawFilter in STATUS_FILTERS ? (rawFilter as StatusFilterKey) : "all");

  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilterKey>(initialFilter);
  const [groupFilter, setGroupFilter] = useState<string>("all");
  const [sort, setSort] = useState<SortKey>("newest");
  const [view, setView] = useState<RosterView>("list");

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Debounced so typing a name costs one request, not one per keystroke — `search` is part of the
  // query key, so every character previously started a new server round-trip.
  const search = useDebouncedValue(searchInput, 350);

  const { data: groups = [] } = useQuery({
    queryKey: ["tl", "groups"],
    queryFn: fetchMyGroups,
    enabled: !!user?.id,
    staleTime: 30_000,
  });

  // Roster-wide counts for the signal band. Scoped server-side to the caller's viewable students
  // (the same AccessScopeService the grid uses), so the band and the roster agree.
  const { data: counts, isLoading: countsLoading } = useQuery({
    queryKey: ["tl", "student-status-counts"],
    queryFn: fetchStudentStatusCounts,
    enabled: !!user?.id,
    staleTime: 60_000,
  });

  // "Needs attention" is blacklisted OR not responding, and the status-counts payload has no such
  // rollup — only the two flags separately. Adding them would double-count anyone carrying both,
  // so the exact figure comes from the grid's own `totalCount` under the `needsAttention` facet:
  // one page of one row, which is what makes it cheap.
  const { data: attentionCount, isLoading: attentionLoading } = useQuery({
    queryKey: ["tl", "students-needing-attention"],
    queryFn: () => fetchStudentGrid({ needsAttention: true, pageNumber: 1, pageSize: 1 }),
    enabled: !!user?.id,
    staleTime: 60_000,
    select: (result) => result.totalCount,
  });

  // Create mapping of groupId -> tracks & courses
  const groupMap = useMemo(() => {
    return new Map(groups.map((g) => [g.id, g]));
  }, [groups]);

  // Build grid query params
  const gridParams = useMemo(() => {
    const params: StudentFilterQueryParams = {
      pageNumber: page,
      pageSize: pageSize,
      search: search.trim(),
      ...SORT_OPTIONS[sort].params,
      ...STATUS_FILTERS[statusFilter].params,
    };

    if (groupFilter !== "all") {
      params.groupId = groupFilter;
    }

    return params;
  }, [page, pageSize, search, groupFilter, statusFilter, sort]);

  const { data: gridData, isLoading } = useQuery({
    queryKey: ["tl", "students-grid", gridParams],
    queryFn: () => fetchStudentGrid(gridParams),
    enabled: !!user?.id,
  });

  const students = gridData?.items || [];
  const hasFilters = statusFilter !== "all" || groupFilter !== "all" || searchInput.trim() !== "";

  const resetFilters = () => {
    setSearchInput("");
    setStatusFilter("all");
    setGroupFilter("all");
    setPage(1);
  };

  /** Every filter change has to return to page 1 — otherwise a narrower result set lands the user on a page that no longer exists. */
  const applyStatus = (value: StatusFilterKey) => {
    setStatusFilter(value);
    setPage(1);
  };

  const openChat = (studentId: string) =>
    navigate({ to: "/messages", search: { dmUserId: studentId } });

  const rosterBody: ReactNode = isLoading ? (
    <RosterSkeleton view={view} />
  ) : students.length === 0 ? (
    // "No matches" and "no students at all" need different words and different exits — offering
    // "clear your filters" to someone with no filters set is a dead end.
    <EmptyState
      icon={GraduationCap}
      title={hasFilters ? "No students match these filters" : "No students in your groups yet"}
      description={
        hasFilters
          ? "Try a different status, group, or search term."
          : "Students appear here once they are added to a group you lead."
      }
      action={
        hasFilters ? (
          <Button variant="outline" size="sm" className="gap-1" onClick={resetFilters}>
            <X className="h-3.5 w-3.5" /> Clear filters
          </Button>
        ) : (
          <Button variant="outline" size="sm" asChild>
            <Link to="/team-leader/groups">Go to My Groups</Link>
          </Button>
        )
      }
    />
  ) : view === "cards" ? (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {students.map((student) => (
        <StudentCard
          key={student.id}
          student={student}
          groupMap={groupMap}
          onChat={() => openChat(student.id)}
        />
      ))}
    </div>
  ) : (
    <Card className="overflow-hidden">
      <RosterHeaderRow />
      {students.map((student) => (
        <StudentRow
          key={student.id}
          student={student}
          groupMap={groupMap}
          onChat={() => openChat(student.id)}
        />
      ))}
    </Card>
  );

  return (
    <div className="mx-auto max-w-7xl space-y-5 p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <GraduationCap className="h-6 w-6" />
        </span>
        <div className="min-w-0">
          <h1 className="text-3xl font-bold tracking-tight">Mentoring Roster</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {counts ? (
              <>
                {counts.total.toLocaleString()} student{counts.total === 1 ? "" : "s"} across the{" "}
                {groups.length} group{groups.length === 1 ? "" : "s"} you lead
              </>
            ) : (
              "Progress, presence and risk for every student in your groups"
            )}
          </p>
        </div>
      </div>

      {/* Signal band. Online + Offline + Never signed in partition the roster exactly, so those
          three always add up to the total — which is the point of showing all three. */}
      <div className="flex flex-wrap items-center gap-x-1 gap-y-2 rounded-xl border bg-card p-3 shadow-sm">
        <div className="flex shrink-0 items-center gap-2.5 pr-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Users className="h-4 w-4" />
          </span>
          <div>
            {countsLoading ? (
              <Skeleton className="h-5 w-10" />
            ) : (
              <p className="text-xl font-extrabold leading-none tabular-nums">
                {counts?.total ?? "—"}
              </p>
            )}
            <p className="mt-1 text-[11px] font-medium leading-none text-muted-foreground">
              Students
            </p>
          </div>
        </div>

        <span className="hidden h-9 w-px shrink-0 bg-border sm:block" />

        <div className="flex flex-1 flex-wrap items-center gap-0.5 sm:pl-2">
          <SignalChip
            dot={PRESENCE.Online.dot}
            label="Online"
            value={counts?.online}
            hint={PRESENCE.Online.hint}
            isLoading={countsLoading}
            isActive={statusFilter === "online"}
            onClick={() => applyStatus("online")}
          />
          <SignalChip
            dot={PRESENCE.Offline.dot}
            label="Offline"
            value={counts?.offline}
            hint={PRESENCE.Offline.hint}
            isLoading={countsLoading}
            isActive={statusFilter === "offline"}
            onClick={() => applyStatus("offline")}
          />
          <SignalChip
            dot={PRESENCE.NeverActive.dot}
            label="Never signed in"
            value={counts?.neverActive}
            hint={PRESENCE.NeverActive.hint}
            isLoading={countsLoading}
            isActive={statusFilter === "never-signed-in"}
            onClick={() => applyStatus("never-signed-in")}
          />
          <SignalChip
            dot="bg-destructive"
            label="Blacklisted"
            value={counts?.blacklisted}
            hint="Blocked from the platform by an admin"
            isLoading={countsLoading}
            isActive={statusFilter === "blacklisted"}
            onClick={() => applyStatus("blacklisted")}
          />
          <SignalChip
            dot="bg-warning"
            label="Not responding"
            value={counts?.notResponding}
            hint="Flagged as unreachable"
            isLoading={countsLoading}
            isActive={statusFilter === "not-responding"}
            onClick={() => applyStatus("not-responding")}
          />
        </div>

        {/* The one number a mentor acts on, pulled out of the row as its own control. */}
        <button
          type="button"
          onClick={() => applyStatus("needs-attention")}
          aria-pressed={statusFilter === "needs-attention"}
          title="Blacklisted or not responding"
          className={[
            "flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-left transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            statusFilter === "needs-attention"
              ? "bg-warning text-warning-foreground"
              : "bg-warning-muted text-warning hover:brightness-95",
          ].join(" ")}
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {attentionLoading ? (
            <Skeleton className="h-4 w-24" />
          ) : (
            <span className="whitespace-nowrap text-[13px] font-bold">
              {attentionCount ?? "—"} need attention
            </span>
          )}
          <ArrowRight className="h-3.5 w-3.5 shrink-0" />
        </button>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1 sm:min-w-[240px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or group…"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(1);
            }}
            className="pl-10 pr-9"
            aria-label="Search students"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => {
                setSearchInput("");
                setPage(1);
              }}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={statusFilter} onValueChange={(v) => applyStatus(v as StatusFilterKey)}>
            <SelectTrigger className="w-[170px]" aria-label="Filter by status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_FILTERS).map(([value, { label }]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={groupFilter}
            onValueChange={(value) => {
              setGroupFilter(value);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[170px]" aria-label="Filter by group">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All groups</SelectItem>
              {groups.map((g) => (
                <SelectItem key={g.id} value={g.id}>
                  {g.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={sort}
            onValueChange={(value) => {
              setSort(value as SortKey);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[170px]" aria-label="Sort students">
              <ArrowUpDown className="mr-1 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(SORT_OPTIONS).map(([value, { label }]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasFilters && (
            <Button variant="ghost" size="sm" className="gap-1" onClick={resetFilters}>
              <X className="h-3.5 w-3.5" /> Clear
            </Button>
          )}

          <ViewToggle view={view} onChange={setView} />
        </div>
      </div>

      {/* Result count. Reading "showing N of M" is how a user tells a narrow filter from an empty
          roster — the header badge alone couldn't say which. */}
      {!isLoading && gridData && students.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Showing <span className="font-medium text-foreground">{students.length}</span> of{" "}
          <span className="font-medium text-foreground">{gridData.totalCount}</span>{" "}
          {gridData.totalCount === 1 ? "student" : "students"}
          {hasFilters && " matching your filters"}
        </p>
      )}

      {rosterBody}

      {!isLoading && gridData && students.length > 0 && (
        <PaginationControls
          pageNumber={page}
          pageSize={pageSize}
          totalPages={gridData.totalPages}
          totalCount={gridData.totalCount}
          hasNextPage={gridData.hasNextPage}
          hasPreviousPage={gridData.hasPreviousPage}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}
